/**
 * The 66 books of the Protestant canon, with chapter counts so we can reject an
 * impossible chapter before spending a network call. Verse counts per chapter
 * are deliberately not held here — that table is large and drifts between
 * versifications; the lookup itself is the authority on whether a verse exists.
 */
export const BOOKS = [
  ['Genesis', 50], ['Exodus', 40], ['Leviticus', 27], ['Numbers', 36], ['Deuteronomy', 34],
  ['Joshua', 24], ['Judges', 21], ['Ruth', 4], ['1 Samuel', 31], ['2 Samuel', 24],
  ['1 Kings', 22], ['2 Kings', 25], ['1 Chronicles', 29], ['2 Chronicles', 36],
  ['Ezra', 10], ['Nehemiah', 13], ['Esther', 10], ['Job', 42], ['Psalms', 150],
  ['Proverbs', 31], ['Ecclesiastes', 12], ['Song of Solomon', 8], ['Isaiah', 66],
  ['Jeremiah', 52], ['Lamentations', 5], ['Ezekiel', 48], ['Daniel', 12], ['Hosea', 14],
  ['Joel', 3], ['Amos', 9], ['Obadiah', 1], ['Jonah', 4], ['Micah', 7], ['Nahum', 3],
  ['Habakkuk', 3], ['Zephaniah', 3], ['Haggai', 2], ['Zechariah', 14], ['Malachi', 4],
  ['Matthew', 28], ['Mark', 16], ['Luke', 24], ['John', 21], ['Acts', 28],
  ['Romans', 16], ['1 Corinthians', 16], ['2 Corinthians', 13], ['Galatians', 6],
  ['Ephesians', 6], ['Philippians', 4], ['Colossians', 4], ['1 Thessalonians', 5],
  ['2 Thessalonians', 3], ['1 Timothy', 6], ['2 Timothy', 4], ['Titus', 3], ['Philemon', 1],
  ['Hebrews', 13], ['James', 5], ['1 Peter', 5], ['2 Peter', 3], ['1 John', 5],
  ['2 John', 1], ['3 John', 1], ['Jude', 1], ['Revelation', 22],
].map(([name, chapters]) => ({ name, chapters }))

/** How people actually type them. */
const ALIASES = {
  gen: 'Genesis', ge: 'Genesis', ex: 'Exodus', exo: 'Exodus', lev: 'Leviticus',
  num: 'Numbers', deut: 'Deuteronomy', dt: 'Deuteronomy', josh: 'Joshua', judg: 'Judges',
  '1sam': '1 Samuel', '2sam': '2 Samuel', '1kgs': '1 Kings', '2kgs': '2 Kings',
  '1chr': '1 Chronicles', '2chr': '2 Chronicles', neh: 'Nehemiah', est: 'Esther',
  ps: 'Psalms', psa: 'Psalms', psalm: 'Psalms', prov: 'Proverbs', pr: 'Proverbs',
  eccl: 'Ecclesiastes', ecc: 'Ecclesiastes', song: 'Song of Solomon',
  sos: 'Song of Solomon', isa: 'Isaiah', jer: 'Jeremiah', lam: 'Lamentations',
  ezek: 'Ezekiel', dan: 'Daniel', hos: 'Hosea', obad: 'Obadiah', jon: 'Jonah',
  mic: 'Micah', nah: 'Nahum', hab: 'Habakkuk', zeph: 'Zephaniah', hag: 'Haggai',
  zech: 'Zechariah', mal: 'Malachi',
  matt: 'Matthew', mt: 'Matthew', mk: 'Mark', lk: 'Luke', jn: 'John',
  rom: 'Romans', '1cor': '1 Corinthians', '2cor': '2 Corinthians', gal: 'Galatians',
  eph: 'Ephesians', phil: 'Philippians', php: 'Philippians', col: 'Colossians',
  '1thess': '1 Thessalonians', '2thess': '2 Thessalonians', '1tim': '1 Timothy',
  '2tim': '2 Timothy', tit: 'Titus', phlm: 'Philemon', heb: 'Hebrews', jas: 'James',
  '1pet': '1 Peter', '2pet': '2 Peter', '1jn': '1 John', '2jn': '2 John', '3jn': '3 John',
  rev: 'Revelation',
}

const norm = (s) => s.toLowerCase().replace(/[.\s]/g, '')

/** Ranked suggestions: exact, then prefix, then alias, then contains. */
export function suggestBooks(query, limit = 6) {
  const q = norm(query)
  if (!q) return []
  const scored = []
  for (const b of BOOKS) {
    const n = norm(b.name)
    if (n === q) scored.push([0, b])
    else if (n.startsWith(q)) scored.push([1, b])
    else if (ALIASES[q] === b.name) scored.push([2, b])
    else if (n.includes(q)) scored.push([3, b])
  }
  return scored.sort((a, b) => a[0] - b[0]).slice(0, limit).map(([, b]) => b)
}

export const findBook = (name) =>
  BOOKS.find((b) => norm(b.name) === norm(name)) ??
  (ALIASES[norm(name)] ? BOOKS.find((b) => b.name === ALIASES[norm(name)]) : undefined)

/** "Psalm 27:14", "Ruth 1:20-21", "1 Cor 13" — best effort, returns null if unsure. */
export function parseReference(input) {
  const m = String(input).trim().match(/^(.+?)\s*(\d+)(?::(\d+)(?:\s*[-–]\s*(\d+))?)?$/)
  if (!m) return null
  const book = findBook(m[1])
  if (!book) return null
  const chapter = Number(m[2])
  if (chapter < 1 || chapter > book.chapters) return null
  return {
    book: book.name,
    chapter,
    verseStart: m[3] ? Number(m[3]) : null,
    verseEnd: m[4] ? Number(m[4]) : null,
  }
}

export function formatReference({ book, chapter, verseStart, verseEnd }) {
  if (!book || !chapter) return ''
  let ref = `${book} ${chapter}`
  if (verseStart) {
    ref += `:${verseStart}`
    if (verseEnd && verseEnd !== verseStart) ref += `-${verseEnd}`
  }
  return ref
}

/**
 * KJV and WEB are public domain and work with no key at all. ESV needs a
 * Crossway API key set server-side. NIV is deliberately absent — Biblica does
 * not license it for this kind of API use, so offering it would be a promise we
 * could not keep.
 */
export const TRANSLATIONS = [
  { id: 'kjv', label: 'King James', note: 'The familiar older English. Public domain.' },
  { id: 'web', label: 'Everyday', note: 'World English Bible — plain modern English. Public domain.' },
  { id: 'esv', label: 'ESV', note: 'English Standard Version. Needs a Crossway API key.' },
]

/**
 * "WEB" means nothing to a worship leader, so it's shown as "Everyday". The
 * underlying id stays WEB — this is only the label, never a claim that it is a
 * different translation. Full name is kept alongside for attribution.
 */
const LABELS = {
  KJV: { short: 'King James', full: 'King James Version' },
  WEB: { short: 'Everyday', full: 'World English Bible' },
  ESV: { short: 'ESV', full: 'English Standard Version' },
}

export const translationLabel = (id, form = 'short') =>
  LABELS[String(id || '').toUpperCase()]?.[form] ?? id ?? ''
